import { Link } from "react-router-dom";
import {
  Search,
  Users,
  Trophy,
  Activity,
  Database,
  Bookmark,
  Sparkles,
  Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function RadarVisual() {
  // Hexagon radar geometry
  const size = 400;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = 150;
  const rings = [0.35, 0.6, 0.8, 1];
  const axes = 6;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / axes;

  const hexPoints = (r: number) =>
    Array.from({ length: axes }, (_, i) => {
      const a = angle(i);
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(" ");

  // Player-like profile values (0..1)
  const values = [0.85, 0.72, 0.9, 0.78, 0.55, 0.7];
  const dataPoints = values
    .map((v, i) => {
      const a = angle(i);
      const r = v * maxR;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    })
    .join(" ");

  // Decorative "the numbers" echo outline (no data meaning)
  const echoValues = [0.7, 0.82, 0.76, 0.66, 0.72, 0.6];
  const echoPoints = echoValues
    .map((v, i) => {
      const a = angle(i);
      const r = v * maxR;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    })
    .join(" ");

  const labels = ["Pace", "Shooting", "Passing", "Dribbling", "Defending", "Physical"];

  return (
    <div className="relative w-full max-w-md mx-auto motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700">
      <div
        aria-hidden="true"
        className="absolute inset-[10%] -z-10 rounded-full blur-3xl opacity-60"
        style={{
          background:
            "radial-gradient(circle at center, hsl(var(--primary-glow) / 0.5), transparent 60%)",
        }}
      />
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full h-auto"
        role="img"
        aria-label="Example player attribute radar"
      >
        {/* Concentric hex rings */}
        {rings.map((f, i) => (
          <polygon
            key={i}
            points={hexPoints(f * maxR)}
            fill="none"
            stroke="hsl(var(--border))"
            strokeWidth={1}
          />
        ))}
        {/* Axis lines */}
        {Array.from({ length: axes }, (_, i) => {
          const a = angle(i);
          return (
            <line
              key={i}
              x1={cx}
              y1={cy}
              x2={cx + maxR * Math.cos(a)}
              y2={cy + maxR * Math.sin(a)}
              stroke="hsl(var(--border))"
              strokeWidth={1}
              opacity={0.6}
            />
          );
        })}
        {/* Decorative echo outline */}
        <polygon
          points={echoPoints}
          fill="none"
          stroke="hsl(var(--muted-foreground) / 0.55)"
          strokeWidth={1.25}
          strokeDasharray="4 5"
          strokeLinejoin="round"
          aria-hidden="true"
        />
        {/* Data polygon */}
        <polygon
          points={dataPoints}
          fill="hsl(var(--primary) / 0.16)"
          className="motion-safe:animate-fade-in"
        />
        <polygon
          points={dataPoints}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray={1}
          strokeDashoffset={1}
          className="animate-draw"
          style={{ filter: "drop-shadow(0 0 6px hsl(var(--primary) / 0.45))" }}
        />
        {/* Vertex dots */}
        {values.map((v, i) => {
          const a = angle(i);
          return (
            <circle
              key={i}
              cx={cx + v * maxR * Math.cos(a)}
              cy={cy + v * maxR * Math.sin(a)}
              r={3.5}
              fill="hsl(var(--primary))"
              stroke="hsl(var(--card))"
              strokeWidth={1.5}
              className="motion-safe:animate-count-pop"
              style={{ transformOrigin: `${cx + v * maxR * Math.cos(a)}px ${cy + v * maxR * Math.sin(a)}px`, animationDelay: `${600 + i * 60}ms` }}
            />
          );
        })}
        {/* Labels */}
        {labels.map((label, i) => {
          const a = angle(i);
          const lr = maxR + 22;
          const x = cx + lr * Math.cos(a);
          const y = cy + lr * Math.sin(a);
          return (
            <text
              key={label}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-muted-foreground"
              style={{ fontSize: 11, fontWeight: 500, fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

const steps = [
  {
    icon: Search,
    title: "Scout",
    desc: "Run live scouting sessions and rate players across key attributes.",
  },
  {
    icon: Users,
    title: "Build the picture",
    desc: "Your ratings join the community to shape each player's profile.",
  },
  {
    icon: Trophy,
    title: "Level up",
    desc: "Earn XP, climb the ranks, and grow your scouting reputation.",
  },
];

const features = [
  { icon: Activity, label: "Live scouting sessions" },
  { icon: Database, label: "Player database", sub: "30+ leagues" },
  { icon: Bookmark, label: "Watchlists" },
  { icon: Sparkles, label: "XP & levels" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/70 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
        <nav className="container mx-auto flex items-center justify-between py-3 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2 group" aria-label="HalftimeScout home">
            <span className="font-display font-bold text-lg tracking-tight">
              HalftimeScout
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" asChild>
              <Link to="/auth">Log in</Link>
            </Button>
            <Button asChild>
              <Link to="/auth">Sign up</Link>
            </Button>
          </div>
        </nav>
      </header>

      <main>
      {/* Hero */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-1">
            <p className="eyebrow !text-primary mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1">
              Community sports scouting
            </p>
            <h1 className="font-display font-bold text-[clamp(2.5rem,7vw,5rem)] tracking-[-0.035em] leading-[1.02]">
              Scout faster.
              <br />
              <span className="text-muted-foreground">Discover smarter.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl">
              Rate players, track your scouting sessions, and build your reputation on a
              community-driven sports scouting platform.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button size="lg" asChild className="text-base">
                <Link to="/auth">Start scouting — free</Link>
              </Button>
              <Button size="lg" variant="ghost" asChild className="text-base">
                <Link to="/auth">Log in</Link>
              </Button>
            </div>
            <p className="mt-8 font-mono text-xs sm:text-[13px] text-muted-foreground tabular-nums flex flex-wrap items-center gap-x-3 gap-y-1">
              18,000+ players <span aria-hidden="true" className="text-border">·</span> 30+ leagues{" "}
              <span aria-hidden="true" className="text-border">·</span> live community scouting
            </p>
          </div>
          <div className="order-2 hidden sm:block">
            <RadarVisual />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-center mb-10 sm:mb-14">
          How it works
        </h2>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="group relative overflow-hidden rounded-2xl border border-border/70 bg-card p-6 sm:p-7 flex flex-col gap-3 shadow-card transition-[transform,border-color,box-shadow] duration-[220ms] ease-out hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated"
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <span className="num-display font-bold text-5xl leading-none text-muted-foreground/25 transition-colors group-hover:text-primary/40">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-lg">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.label}
                className="rounded-xl border border-border/70 bg-card p-4 sm:p-5 flex items-center gap-3 transition-colors duration-[140ms] hover:border-primary/30"
              >
                <div className="h-9 w-9 rounded-lg bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm leading-tight">{f.label}</p>
                  {f.sub && (
                    <p className="text-xs text-muted-foreground mt-0.5">{f.sub}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="relative isolate rounded-3xl border border-border/70 bg-card overflow-hidden text-center px-6 py-16 sm:py-24 shadow-elevated">
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 opacity-70"
            style={{
              background:
                "radial-gradient(ellipse 70% 60% at 50% 0%, hsl(var(--primary-glow) / 0.28), transparent 70%)",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 opacity-[0.06] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            }}
          />
          <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-[-0.03em]">
            Ready to start scouting?
          </h2>
          <div className="mt-8 flex justify-center">
            <Button size="lg" asChild className="text-base">
              <Link to="/auth">Create your free account</Link>
            </Button>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60 mt-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold">HalftimeScout</span>
          </div>
          <div className="flex items-center gap-5 sm:gap-6 text-sm text-muted-foreground/80">
            <a href="/faq.html" className="hover:text-foreground transition-colors">FAQ</a>
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms
            </Link>
            <a
              href="https://instagram.com/halftimescout"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:text-foreground transition-colors"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href="https://x.com/halftimescout"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
              className="hover:text-foreground transition-colors"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
          </div>
          <p className="text-sm text-muted-foreground">© 2026 Halftime Scout.</p>
        </div>
      </footer>
    </div>
  );
}